import { useState, useEffect, useRef } from 'react';
import { categoryType, useGetAiWorkersByCategoryQuery, useGetAgentHisotryMutation, useGetAiWorkerUsageByIdMutation, RootState } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';

export interface UXGenieTemplate {
  aiworker_usage_id: string;
  aiworker_id: string;
  title: string;
  aiworker_name: string;
  status: string;
  date: string;
  [key: string]: any;
}

export interface UseUXGenieTemplateSelectionResult {
  // Worker selection
  selectedUXGenieWorkerId: string;
  setSelectedUXGenieWorkerId: (id: string) => void;
  uxgenieWorkers: any[];
  
  // Template selection
  selectedUXGenieTemplateId: string;
  setSelectedUXGenieTemplateId: (id: string) => void;
  uxgenieTemplates: UXGenieTemplate[];
  
  // Loading state
  isLoadingTemplates: boolean;
}

export function useUXGenieTemplateSelection(
  initialTemplateId?: string
): UseUXGenieTemplateSelectionResult {
  const [selectedUXGenieTemplateId, setSelectedUXGenieTemplateId] = useState<string>(initialTemplateId || '');
  const [selectedUXGenieWorkerId, setSelectedUXGenieWorkerId] = useState<string>('');
  const [uxgenieTemplates, setUxgenieTemplates] = useState<UXGenieTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const initialTemplateLoadedRef = useRef<boolean>(false);
  const token = useSelector((state: RootState) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);

  const { data: uxgenieWorkers = [] } = useGetAiWorkersByCategoryQuery({ category: categoryType.UXGENIE }, { skip: !token });
  const [getUsageHistory] = useGetAgentHisotryMutation();
  const [getTemplateById] = useGetAiWorkerUsageByIdMutation();

  useEffect(() => {
    if (token) {
      setSkip(false);
    }
  }, [token, skip]);

  // When initial template ID is provided, fetch the template to get its worker ID
  // This auto-populates both the worker dropdown and template dropdown
  useEffect(() => {
    const loadInitialTemplate = async () => {
      if (initialTemplateId && !initialTemplateLoadedRef.current && uxgenieWorkers.length > 0) {
        initialTemplateLoadedRef.current = true;
        console.log('📦 Loading initial template:', initialTemplateId);
        
        try {
          const response = await getTemplateById(initialTemplateId).unwrap();
          
          // Handle array or object response
          let templateData = response;
          if (Array.isArray(response) && response.length > 0) {
            templateData = response[0];
          } else if (response?.data) {
            templateData = response.data;
          }
          
          // Get the worker ID from the template
          const workerId = templateData?.aiworker_id;
          
          if (workerId) {
            console.log('✅ Found worker ID for template:', workerId);
            // Set the worker ID first (this will trigger template list fetch)
            setSelectedUXGenieWorkerId(workerId);
            // Set the template ID
            setSelectedUXGenieTemplateId(initialTemplateId);
          } else {
            console.warn('⚠️ Template found but no worker ID:', templateData);
          }
        } catch (error) {
          console.error('❌ Failed to load initial template:', error);
        }
      }
    };
    
    loadInitialTemplate();
  }, [initialTemplateId, uxgenieWorkers, getTemplateById]);

  // Update template ID when it changes from parent (without re-fetching worker if already loaded)
  useEffect(() => {
    if (initialTemplateId && initialTemplateLoadedRef.current) {
      setSelectedUXGenieTemplateId(initialTemplateId);
    }
  }, [initialTemplateId]);

  // Fetch templates when a UXGenie worker is selected
  useEffect(() => {
    const fetchTemplates = async () => {
      if (selectedUXGenieWorkerId) {
        setIsLoadingTemplates(true);
        try {
          const response = await getUsageHistory({
            pagination: {
              page: 1,
              per_page: 50
            },
            filters: [
              {
                aiworker_type: 'uxgenie_logs',
                condition: 'contains'
              },
              {
                aiworker_id: selectedUXGenieWorkerId,
                condition: 'contains'
              }
            ]
          }).unwrap();

          // Extract aiworkers_conversations from the nested response
          const templates = response?.aiworkers_conversations || [];
          // Filter to only include templates that match the selected worker ID
          const filteredTemplates = templates.filter(
            (t: any) => t.aiworker_id === selectedUXGenieWorkerId
          );
          setUxgenieTemplates(filteredTemplates);
        } catch (error) {
          console.error('Failed to fetch UXGenie templates:', error);
          setUxgenieTemplates([]);
        } finally {
          setIsLoadingTemplates(false);
        }
      } else {
        setUxgenieTemplates([]);
        setSelectedUXGenieTemplateId('');
      }
    };

    fetchTemplates();
  }, [selectedUXGenieWorkerId, getUsageHistory]);

  return {
    selectedUXGenieWorkerId,
    setSelectedUXGenieWorkerId,
    uxgenieWorkers,
    selectedUXGenieTemplateId,
    setSelectedUXGenieTemplateId,
    uxgenieTemplates,
    isLoadingTemplates
  };
}

export default useUXGenieTemplateSelection;

